class FindMyMount extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-fmm-form]');
    this.results = this.querySelector('[data-fmm-results]');
    this.summary = this.querySelector('[data-fmm-summary]');
    this.limit = parseInt(this.getAttribute('data-limit') || '0', 10);
    this.shopDomain = this.getAttribute('data-shop-domain');
    this.storefrontToken = this.getAttribute('data-storefront-token');
    this.products = null;

    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.runSearch();
    });
  }

  async ensureProductsLoaded() {
    if (this.products) return this.products;
    this.summary.textContent = 'Loading catalog...';
    this.products = await this.fetchAllProducts();
    return this.products;
  }

  async fetchAllProducts() {
    const all = [];
    let cursor = null;
    let hasNextPage = true;

    const query = `
      query FindMyMountProducts($cursor: String) {
        products(first: 100, after: $cursor) {
          edges {
            cursor
            node {
              title
              handle
              featuredImage { url }
              tags
              maxWeightKg: metafield(namespace: "specs", key: "max_weight_kg") { value }
              minVesaW: metafield(namespace: "specs", key: "min_vesa_width_mm") { value }
              minVesaH: metafield(namespace: "specs", key: "min_vesa_height_mm") { value }
              maxVesaW: metafield(namespace: "specs", key: "max_vesa_width_mm") { value }
              maxVesaH: metafield(namespace: "specs", key: "max_vesa_height_mm") { value }
            }
          }
          pageInfo { hasNextPage }
        }
      }
    `;

    while (hasNextPage) {
      let json;
      try {
        const res = await fetch(`https://${this.shopDomain}/api/2025-01/graphql.json`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': this.storefrontToken,
          },
          body: JSON.stringify({ query, variables: { cursor } }),
        });
        json = await res.json();
      } catch (e) {
        break;
      }

      const edges = (json.data && json.data.products && json.data.products.edges) || [];
      edges.forEach((edge) => {
        const n = edge.node;
        if (n.maxWeightKg && n.maxWeightKg.value) {
          all.push({
            title: n.title,
            url: '/products/' + n.handle,
            image: n.featuredImage ? n.featuredImage.url : '',
            maxWeightKg: parseFloat(n.maxWeightKg.value),
            minVesaW: n.minVesaW && n.minVesaW.value !== null ? parseFloat(n.minVesaW.value) : null,
            minVesaH: n.minVesaH && n.minVesaH.value !== null ? parseFloat(n.minVesaH.value) : null,
            maxVesaW: n.maxVesaW && n.maxVesaW.value !== null ? parseFloat(n.maxVesaW.value) : null,
            maxVesaH: n.maxVesaH && n.maxVesaH.value !== null ? parseFloat(n.maxVesaH.value) : null,
            tags: n.tags || [],
          });
        }
      });

      hasNextPage = !!(json.data && json.data.products && json.data.products.pageInfo && json.data.products.pageInfo.hasNextPage);
      cursor = edges.length ? edges[edges.length - 1].cursor : null;
    }

    return all;
  }

  async runSearch() {
    const weightInput = this.form.querySelector('[name="fmm_weight"]');
    const vesaInput = this.form.querySelector('[name="fmm_vesa"]');
    const typeInput = this.form.querySelector('[name="fmm_type"]');

    const weight = weightInput && weightInput.value ? parseFloat(weightInput.value) : null;
    const vesa = vesaInput && vesaInput.value ? vesaInput.value : '';
    const type = typeInput && typeInput.value ? typeInput.value : '';

    if (weight === null || isNaN(weight) || weight <= 0) {
      this.summary.textContent = this.getAttribute('data-error-weight') || 'Enter your TV or display weight to search.';
      this.results.innerHTML = '';
      return;
    }

    let vesaW = null;
    let vesaH = null;
    if (vesa) {
      const parts = vesa.split('x');
      vesaW = parseInt(parts[0], 10);
      vesaH = parseInt(parts[1], 10);
    }

    const products = await this.ensureProductsLoaded();

    const matches = products.filter((p) => {
      if (p.maxWeightKg == null || p.maxWeightKg < weight) return false;

      if (vesaW !== null) {
        if (p.maxVesaW == null || p.maxVesaH == null) {
          return false;
        }
        const minW = p.minVesaW != null ? p.minVesaW : 0;
        const minH = p.minVesaH != null ? p.minVesaH : 0;
        const directFit = vesaW >= minW && vesaW <= p.maxVesaW && vesaH >= minH && vesaH <= p.maxVesaH;
        const rotatedFit = vesaH >= minW && vesaH <= p.maxVesaW && vesaW >= minH && vesaW <= p.maxVesaH;
        if (!directFit && !rotatedFit) return false;
      }

      if (type && (!p.tags || !p.tags.includes(type))) return false;

      return true;
    });

    this.renderResults(matches);
  }

  renderResults(matches) {
    const total = matches.length;
    const shown = this.limit > 0 ? matches.slice(0, this.limit) : matches;

    if (total === 0) {
      this.summary.textContent = this.getAttribute('data-empty-message') || 'No mounts match those specs yet — message us on WhatsApp and we’ll help you find the right one.';
      this.results.innerHTML = '';
      return;
    }

    this.summary.textContent = (this.getAttribute('data-results-label') || '{count} mounts found').replace('{count}', total);

    this.results.innerHTML = shown
      .map((p) => `
        <li class="grid__item">
          <div class="card-wrapper product-card-wrapper underline-links-hover">
            <div class="card card--card">
              <div class="card__inner ratio" style="--ratio-percent: 100%;">
                <div class="card__media">
                  <div class="media media--transparent">
                    <img src="${p.image || ''}" alt="${this.escapeHtml(p.title)}" loading="lazy" width="400" height="400">
                  </div>
                </div>
              </div>
              <div class="card__content">
                <div class="card__information">
                  <h3 class="card__heading h5">
                    <a href="${p.url}" class="full-unstyled-link">${this.escapeHtml(p.title)}</a>
                  </h3>
                  <div class="card-information">
                    <span class="caption-large light">Up to ${p.maxWeightKg} kg${p.maxVesaW ? ' &middot; VESA ' + p.maxVesaW + 'x' + p.maxVesaH + 'mm' : ''}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </li>
      `)
      .join('');
  }

  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str || '';
    return div.innerHTML;
  }
}

customElements.define('find-my-mount', FindMyMount);
